// EPSE Saved Simulations List
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  History, Trash2, Eye, Battery, TrendingDown, AlertTriangle, Clock, Loader2 
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { nl } from 'date-fns/locale';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface Simulation {
  id: string;
  name: string;
  created_at: string;
  status: string;
  dispatch_mode: string;
  bess_config: {
    capacity_kwh: number;
    power_kw: number;
    efficiency_charge: number;
    efficiency_discharge: number;
    soc_min: number;
    soc_max: number;
  };
  kpis?: {
    peak_reduction_kw: number;
    peak_reduction_percent: number;
    peak_overruns_count: number;
    peak_overruns_duration_hours: number;
    bess_cycles: number;
    bess_throughput_kwh: number;
    energy_above_limit_kwh: number;
  };
  results?: any;
}

interface EPSESavedSimulationsProps {
  simulations: Simulation[] | undefined;
  isLoading: boolean;
  onDelete: (id: string) => Promise<void>;
  onView: (simulation: Simulation) => void;
}

export function EPSESavedSimulations({
  simulations,
  isLoading,
  onDelete,
  onView,
}: EPSESavedSimulationsProps) {
  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-12 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!simulations || simulations.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Opgeslagen Simulaties
          </CardTitle>
          <CardDescription>
            Bekijk en vergelijk eerdere simulatie resultaten
          </CardDescription>
        </CardHeader>
        <CardContent className="py-12 text-center">
          <History className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">
            Nog geen simulaties opgeslagen. Ga naar het Simulatie tabblad om een simulatie op te slaan.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <History className="h-5 w-5" />
          Opgeslagen Simulaties
          <Badge variant="secondary">{simulations.length}</Badge>
        </CardTitle>
        <CardDescription>
          Bekijk en vergelijk eerdere simulatie resultaten
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Naam</TableHead>
                <TableHead>Datum</TableHead>
                <TableHead>BESS Config</TableHead>
                <TableHead>Piekreductie</TableHead>
                <TableHead>Overschrijdingen</TableHead>
                <TableHead>Cycli</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Acties</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {simulations.map((sim) => (
                <TableRow key={sim.id}>
                  <TableCell className="font-medium">{sim.name || 'Naamloos'}</TableCell>
                  <TableCell className="text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatDistanceToNow(new Date(sim.created_at), { addSuffix: true, locale: nl })}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="flex items-center gap-1 w-fit">
                      <Battery className="h-3 w-3" />
                      {sim.bess_config.capacity_kwh} kWh / {sim.bess_config.power_kw} kW
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {sim.kpis ? (
                      <div className="flex items-center gap-1">
                        <TrendingDown className="h-3 w-3 text-green-500" />
                        <span className="font-medium">{sim.kpis.peak_reduction_kw.toFixed(0)} kW</span>
                        <span className="text-muted-foreground text-xs">
                          ({sim.kpis.peak_reduction_percent.toFixed(1)}%)
                        </span>
                      </div>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    {sim.kpis ? (
                      <div className="flex items-center gap-1">
                        <AlertTriangle className={`h-3 w-3 ${sim.kpis.peak_overruns_count > 0 ? 'text-amber-500' : 'text-green-500'}`} />
                        <span>{sim.kpis.peak_overruns_count}</span>
                        <span className="text-muted-foreground text-xs">
                          ({sim.kpis.peak_overruns_duration_hours.toFixed(1)}u)
                        </span>
                      </div>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    {sim.kpis ? (
                      <span className="font-medium">{sim.kpis.bess_cycles.toFixed(2)}</span>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <Badge variant={sim.status === 'completed' ? 'default' : sim.status === 'failed' ? 'destructive' : 'secondary'}>
                      {sim.status === 'completed' ? 'Voltooid' : sim.status === 'failed' ? 'Mislukt' : 'Bezig'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onView(sim)}
                        title="Bekijken"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" title="Verwijderen">
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Simulatie verwijderen?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Weet u zeker dat u "{sim.name || 'deze simulatie'}" wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annuleren</AlertDialogCancel>
                            <AlertDialogAction onClick={() => onDelete(sim.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                              Verwijderen
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
